import React, { useMemo, useState } from 'react';
import { useQueries } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Check, FolderOpen, Plus } from 'lucide-react';
import { Button } from 'components/ui/button';
import { Input } from 'components/ui/input';
import Modal from 'components/ui/Modal';
import { cn } from 'lib/utils';
import * as CollectionsModule from 'modules/collections';
import * as CollectionsApi from 'modules/collections/api';
import type { CollectionItemType } from 'modules/collections/types';

interface Props {
  open: boolean;
  onClose: () => void;
  itemType: CollectionItemType;
  itemId: string;
  /** Optional label of the resource being added (shown in the header). */
  itemLabel?: string;
}

/** Checked = the resource is already in that collection. Includes an inline create-new row. */
export const AddToCollectionModal: React.FC<Props> = ({ open, onClose, itemType, itemId, itemLabel }) => {
  const { t } = useTranslation();
  const [search, setSearch] = useState('');
  const [newName, setNewName] = useState('');

  const { collections, isLoading } = CollectionsModule.Hooks.useOwned({ enabled: open });
  const addItem = CollectionsModule.Hooks.useAddItem();
  const removeItem = CollectionsModule.Hooks.useRemoveItem();
  const createCollection = CollectionsModule.Hooks.useCreate();

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return collections.filter((c) => !q || c.name.toLowerCase().includes(q));
  }, [collections, search]);

  // There's no batch "which collections contain this item" endpoint (see
  // apps/api/src/modules/collections/collections.controller.ts) — one detail
  // fetch per collection is the least-bad option available from here. Two
  // mitigations: (1) scope the fan-out to the *visible* (search-filtered)
  // collections rather than every owned one, so narrowing the search also
  // narrows the request count; (2) cache for a minute so reopening this
  // modal for another item doesn't immediately refire every request.
  const detailQueries = useQueries({
    queries: (open ? filtered : []).map((c) => ({
      queryKey: ['collections', 'single', c.id],
      queryFn: async () => (await CollectionsApi.single(c.id)).data.data,
      staleTime: 60_000,
    })),
  });

  const membership = useMemo(() => {
    const map: Record<string, boolean> = {};
    filtered.forEach((c, i) => {
      const items = detailQueries[i]?.data?.items ?? [];
      map[c.id] = items.some((it) => it.type === itemType && it.itemId === itemId);
    });
    return map;
  }, [filtered, detailQueries, itemType, itemId]);

  const pending = addItem.isPending || removeItem.isPending || createCollection.isPending;

  const toggle = async (collectionId: string, contains: boolean) => {
    try {
      if (contains) {
        await removeItem.mutateAsync({ collectionId, type: itemType, itemId });
      } else {
        await addItem.mutateAsync({ collectionId, type: itemType, itemId });
      }
    } catch {
      /* hook toast */
    }
  };

  const handleCreate = async () => {
    const name = newName.trim();
    if (!name) return;
    try {
      const created = await createCollection.mutateAsync({ name });
      if (created?.id) {
        await addItem.mutateAsync({ collectionId: created.id, type: itemType, itemId });
      }
      setNewName('');
    } catch {
      /* hook toast */
    }
  };

  return (
    <Modal isOpen={open} onClose={onClose} title={t('add_to_collection')}>
      <div className="space-y-3">
        {itemLabel && (
          <p className="text-sm text-muted-foreground">
            {t('collections.adding')} <span className="font-medium text-foreground">{itemLabel}</span>
          </p>
        )}

        <Input
          placeholder={t('collections.search_collections')}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />

        <div className="max-h-[40vh] overflow-y-auto divide-y">
          {isLoading ? (
            <p className="py-6 text-center text-sm text-muted-foreground">{t('loading')}</p>
          ) : filtered.length === 0 ? (
            <p className="py-6 text-center text-sm text-muted-foreground">
              {collections.length === 0 ? t('collections.none_create_below') : t('collections.none_match')}
            </p>
          ) : (
            filtered.map((c) => {
              const contains = membership[c.id] ?? false;
              return (
                <button
                  key={c.id}
                  type="button"
                  onClick={() => toggle(c.id, contains)}
                  disabled={pending}
                  className="flex w-full items-center justify-between gap-3 py-2 text-left disabled:opacity-60"
                >
                  <div className="flex min-w-0 items-center gap-3">
                    <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-muted">
                      <FolderOpen className="h-4 w-4 text-muted-foreground" />
                    </div>
                    <div className="min-w-0">
                      <p className="truncate text-sm font-medium">{c.name}</p>
                      <p className="text-xs text-muted-foreground">{t('collections.items_count', { count: c.itemCount })}</p>
                    </div>
                  </div>
                  <span
                    className={cn(
                      'flex h-5 w-5 shrink-0 items-center justify-center rounded border',
                      contains ? 'border-primary-600 bg-primary text-primary-foreground' : 'border-muted-foreground/30'
                    )}
                  >
                    {contains && <Check className="h-3.5 w-3.5" />}
                  </span>
                </button>
              );
            })
          )}
        </div>

        {/* Inline create-new-collection row */}
        <div className="flex items-center gap-2 border-t pt-3">
          <Input
            placeholder={t('collections.new_collection_name')}
            value={newName}
            onChange={(e) => setNewName(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                handleCreate();
              }
            }}
          />
          <Button variant="outline" onClick={handleCreate} disabled={!newName.trim() || pending}>
            <Plus className="mr-1 h-4 w-4" /> {t('collections.create')}
          </Button>
        </div>

        <div className="flex justify-end pt-1">
          <Button onClick={onClose}>{t('collections.done')}</Button>
        </div>
      </div>
    </Modal>
  );
};

export default AddToCollectionModal;
